import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Subject } from "rxjs";
import { User } from "ng/src/app/users/shared/user.model";
import { ServiceWorkerService } from "service-worker/service-worker.service";
import { ServiceWorkerEvents } from "service-worker/service-events";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { MaintenanceDetails } from "ng/src/app/shared/model/logbook.model";
import { Preferences } from "ng/src/app/core/preferences";
import { AuthClientService } from "ng/src/app/authentication/auth-client.service";
import { Utils } from "ng/src/app/core/utils";
import { Location } from "@angular/common";

@Injectable({
	providedIn: 'root'
})
export class AppStateService {
	private clientTimeOffsetMs = 0;
	private static deletionEnabledForLogs = new Set<number>();
	private _selectedTheme?: 'Light' | 'Dark' | 'Default';
	private static hoverMQL = window.matchMedia('(hover: hover) and (pointer: fine)');

	static isLocalHost = window.location.hostname == 'localhost';
	static isProdEnvironment = ['localhost', 'logbook-test.doozer.com'].includes(window.location.hostname) ? false : true;
	static modifierKey = navigator.platform.startsWith('Mac') ? 'Cmd' : 'Ctrl';
	static usingFirefox = navigator.userAgent.includes('Firefox');
	static usingSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
	static sessionUUID = Utils.generateUniqueId();
	
	private renderer: Renderer2;
	
	private readonly locationChangedSubject = new Subject<{url: string, state: unknown}>();
	
	/**
	 * Provides access to the change events when Angular's location services provides an update to the "onUrlChange" function.
	 * Using this allows only one event to be registered that goes to multiple observables as the service
	 * provides no way of unsubscribing.
	 */
	readonly locationChanged$ = this.locationChangedSubject.asObservable();
	
	get isSwitchedUser() {
		return AppStateService.isSwitchedUser
	}
	
	get isAdmin() {
		return AppStateService.isAdmin;
	}
	
	get hasAdminUiFlag() {
		return AppStateService.hasAdminUiFlag;
	}
	
	set hasAdminUiFlag(val) {
		AppStateService.hasAdminUiFlag = val;
	}

	get selectedTheme() {
		return this._selectedTheme;
	}
	set selectedTheme(theme) {
		if (!theme || theme == 'Default')
			theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'Dark' : 'Light';
		
		if (this.selectedTheme == theme)
			return;
		
		this._selectedTheme = theme;
		
		let bootstrapStylesheet = '/webpack/bootswatch/cerulean.bootstrap.min.css',
			primeNgStylesheet = '/webpack/primeng/saga-blue/theme.css';
		
		if (theme == 'Dark') {
			bootstrapStylesheet = '/webpack/bootswatch/darkly.bootstrap.custom.css',
			primeNgStylesheet = '/webpack/primeng/viva-dark/theme.css';
		}
		
		$('link[title="theme"]').attr('href', bootstrapStylesheet);
		document.getElementById('primeng-theme-link')!.setAttribute('href', primeNgStylesheet);
		
		if (theme === 'Dark') {
			this.renderer.removeClass(document.body, 'lb-light-theme');
			this.renderer.addClass(document.body, 'lb-dark-theme');
		}
		else {
			this.renderer.removeClass(document.body, 'lb-dark-theme');
			this.renderer.addClass(document.body, 'lb-light-theme');
		}
	}

	get currentUser$() {
		return AppStateService.currentUser$
	}
	
	get currentUser(): User | undefined {
		return AppStateService.currentUser
	}
	set currentUser(user) {
		AppStateService.currentUser = user;
	}
	
	get sessionUUID() {
		return AppStateService.sessionUUID;
	}
	
	get preferences() {
		return AppStateService.preferences;
	}
	
	constructor(
		authService: AuthClientService,
		workerService: ServiceWorkerService,
		rendererFactory: RendererFactory2,
		locationService: Location,
		@Inject('$rootScope') private $rootScope: LogBookRootScopeService
	) {
		locationService.onUrlChange((url, state) => this.locationChangedSubject.next({url, state}));
		
		this.renderer = rendererFactory.createRenderer(null, null);
		this.currentUser$.subscribe((user) => {
			 if (workerService.ready)
				workerService.sendEvent(ServiceWorkerEvents.USER_AUTHENTICATED, this.currentUser?.id)
		})

		workerService.registerForEvent<number | undefined>(ServiceWorkerEvents.USER_AUTHENTICATED)
			.subscribe((id) => {
				// we will end up in a reload loop if we haven't set the user yet.
				if (this.currentUser?.id === id || !AppStateService.isUserSetYet)
					return;

				new Promise<void>(r => {
					window.location.hash = '#/';
					r();
				})
					.then(() => {
						window.location.reload();
					})
			})
		
		workerService.registerForEvent(ServiceWorkerEvents.USER_AUTHORIZATION_ERROR)
			.subscribe(() => {
				console.log('Received authentication error from service worker.');
				this.$rootScope?.authenticate()?.catch(() => authService.onUnauthenticated());
			});
		
		if (AppStateService.usingSafari) {
			this.renderer.addClass(document.body, 'using-safari');
		}
		else if (AppStateService.usingFirefox) {
			this.renderer.addClass(document.body, 'using-firefox');
		}
	}

	// TODO: Remove this and use the template ref variable for the fab component in the new ng navbar
	private static closeFabSubject = new Subject();
	static closeFab$ = AppStateService.closeFabSubject.asObservable();
	static closeFab($event?: Event) {
		this.closeFabSubject.next($event);
	}
	
	getClientTimeOffset() {
		return this.clientTimeOffsetMs;
	}
	
	setClientTimeOffset(dateHeader: string) {
		let clientTime = new Date().getTime(),
			offset = new Date(dateHeader).getTime() - clientTime;
		
		if (Math.abs(offset) > 1000)
			this.clientTimeOffsetMs = offset;
	}

	registerMediaQuery(mediaQuery: string, onChangeCallback: (matches: boolean) => void) {
		let mql = window.matchMedia(mediaQuery);
		mql.onchange = e => onChangeCallback(e.matches);

		onChangeCallback(mql.matches);

		const unregisterFunction = () => {
			mql.onchange = null;
		};

		// Execute this in your component's onDestroy hook
		return unregisterFunction;
	}

	updateMaintenanceMode(value: { maintenanceInfo: MaintenanceDetails }) {
		try {
			const dt = new Date(value.maintenanceInfo['logbook.maintenance.expectedEnd']!);
			if (dt && !isNaN(dt.getTime()))
				value.maintenanceInfo.expectedEndTime = dt;
		} catch(e){}
		this.$rootScope.$broadcast('maintenanceInfo', value.maintenanceInfo || {});
	}
	
	private static _isAdmin?: boolean;
	private static readonly _currentUser$ = new BehaviorSubject<User | undefined>(undefined);
	private static isUserSetYet: boolean = false;

	static readonly currentUser$ = AppStateService._currentUser$.asObservable();
	
	static readonly preferences = new Preferences();

	static get currentUser() {
		return this._currentUser$.getValue();
	}

	static set currentUser(val) {
		this.isUserSetYet = true;
		this.preferences.key = val?.id?.toString();
		this._currentUser$.next(val);
	}
	
	static get isSwitchedUser() {
		return this.currentUser?.extendedData?.switchedUser === 'true';
	}
	
	static get hasAdminUiFlag() {
		return window.localStorage.getItem('show-admin-navbar') === 'true';
	}
	
	static set hasAdminUiFlag(val) {
		window.localStorage.setItem('show-admin-navbar', val ? 'true' : 'false')
	}
	
	static get isAdmin(): boolean {
		if (this._isAdmin !== undefined)
			return this._isAdmin;
		
		if (!this.currentUser) return false;
		this._isAdmin = this.currentUser.roles.some(r => r.id === 1);
		return this._isAdmin;
	}

	static urlSearchParamsHas(param: string) {
		return new URLSearchParams(window.location.hash).has(param);
	}

	static urlSearchParamsGet(param: string) {
		return new URLSearchParams(window.location.hash).get(param);
	}

	static get hoverSupported() {
		return this.hoverMQL.matches;
	}

	static get screenAction() {
		return this.hoverSupported ? 'Click' : 'Tap';
	}

	static isNoteDeletionEnabledForLog(id: number) : boolean {
		return this.deletionEnabledForLogs.has(id);
	}
	static setDeletionEnabledForLog(id: number, enabled: boolean = true) {
		enabled ? this.deletionEnabledForLogs.add(id) : this.deletionEnabledForLogs.delete(id);
	}
}